import { z } from "zod";

// Product Attribute Form Schema
export const productAttributeFormSchema = z.object({
  productId: z.string().min(1, { message: "Product ID is required." }),
  name: z.string()
    .min(2, { message: "Name must be at least 2 characters." })
    .max(50, { message: "Name must be less than 50 characters." })
    .regex(/^[a-z0-9_-]+$/, {
      message: "Name must contain only lowercase letters, numbers, hyphens, and underscores."
    }),
  displayName: z.string()
    .min(2, { message: "Display name must be at least 2 characters." })
    .max(100, { message: "Display name must be less than 100 characters." }),
  type: z.enum(['color', 'size', 'material', 'other']).default('color'),
  isActive: z.boolean().default(true),
});

// Product Attribute Value Form Schema
export const productAttributeValueFormSchema = z.object({
  value: z.string()
    .min(1, { message: "Value is required." })
    .max(100, { message: "Value must be less than 100 characters." }),
  displayValue: z.string()
    .min(1, { message: "Display value is required." })
    .max(100, { message: "Display value must be less than 100 characters." })
    .optional(),
  colorCode: z.string()
    .regex(/^#[0-9A-Fa-f]{6}$/, {
      message: "Color code must be a valid hex color (e.g., #FF0000)."
    })
    .optional()
    .or(z.literal('')),
  sortOrder: z.coerce.number()
    .int({ message: "Sort order must be a whole number." })
    .min(0, { message: "Sort order must be 0 or greater." })
    .default(0),
  isActive: z.boolean().default(true),
});

// Product Attribute Create Request Schema
export const productAttributeCreateRequestSchema = productAttributeFormSchema;

// Product Attribute Update Request Schema
export const productAttributeUpdateRequestSchema = productAttributeFormSchema.partial().omit({ productId: true });

// Product Attribute Value Create Request Schema
export const productAttributeValueCreateRequestSchema = productAttributeValueFormSchema.extend({
  attributeId: z.number().int().positive({ message: "Attribute ID is required." }),
});

// Product Attribute Value Update Request Schema
export const productAttributeValueUpdateRequestSchema = productAttributeValueFormSchema.partial();

// Search and Filter Schemas
export const productAttributeSearchSchema = z.object({
  searchTerm: z.string().min(1, { message: "Search term is required." }),
  page: z.coerce.number().int().min(0).default(0),
  size: z.coerce.number().int().min(1).max(100).default(20),
  sort: z.enum(['id,asc', 'id,desc', 'name,asc', 'name,desc', 'createdAt,asc', 'createdAt,desc']).default('id,asc'),
});

export const productAttributeValueSearchSchema = z.object({
  attributeId: z.number().int().positive({ message: "Attribute ID is required." }),
  page: z.coerce.number().int().min(0).default(0),
  size: z.coerce.number().int().min(1).max(100).default(20),
  sort: z.enum(['sortOrder,asc', 'sortOrder,desc', 'value,asc', 'value,desc', 'id,asc', 'id,desc']).default('sortOrder,asc'),
});

// Export types
export type ProductAttributeFormValues = z.infer<typeof productAttributeFormSchema>;
export type ProductAttributeValueFormValues = z.infer<typeof productAttributeValueFormSchema>;
export type ProductAttributeCreateRequest = z.infer<typeof productAttributeCreateRequestSchema>;
export type ProductAttributeUpdateRequest = z.infer<typeof productAttributeUpdateRequestSchema>;
export type ProductAttributeValueCreateRequest = z.infer<typeof productAttributeValueCreateRequestSchema>;
export type ProductAttributeValueUpdateRequest = z.infer<typeof productAttributeValueUpdateRequestSchema>;
export type ProductAttributeSearchParams = z.infer<typeof productAttributeSearchSchema>;
export type ProductAttributeValueSearchParams = z.infer<typeof productAttributeValueSearchSchema>;
